import React, { useEffect, useState, useMemo } from 'react';
import { Table, Button, Modal, Form, Input, Tag, message, Popconfirm, Checkbox, Space, Card } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import axios from 'axios';
import API_BASE_URL from '../../config/api';
import { usePermission } from '../../context/PermissionContext';

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const RoleManagement = () => {
  const [roles, setRoles] = useState([]);
  const [groups, setGroups] = useState([]); // permission catalog แบบ group
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const { can } = usePermission();

  const fetchRoles = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/roles`, { headers: authHeaders() });
      setRoles(res.data?.roles || []);
    } catch {
      message.error('โหลดบทบาทล้มเหลว');
    }
  };

  const fetchCatalog = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/roles/permissions`, { headers: authHeaders() });
      setGroups(res.data?.groups || {});
    } catch {
      setGroups({});
    }
  };

  useEffect(() => {
    fetchRoles();
    fetchCatalog();
  }, []);

  // map รหัสสิทธิ์ -> ชื่อภาษาไทย (จาก catalog ที่โหลดจาก Backend)
  const permNameMap = useMemo(() => {
    const m = {};
    Object.values(groups).forEach((perms) => {
      perms.forEach((p) => {
        m[p.code] = p.name;
      });
    });
    return m;
  }, [groups]);

  const permissionTag = (code) => (
    <Tag key={code} style={{ marginBottom: 4 }}>{permNameMap[code] || code}</Tag>
  );

  if (!can('user.assign_permissions')) {
    return (
      <Card style={{ marginTop: 24, textAlign: 'center' }}>
        คุณไม่มีสิทธิ์เข้าถึงหน้านี้ (ต้องมีสิทธิ์: user.assign_permissions)
      </Card>
    );
  }

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (role) => {
    setEditing(role);
    form.setFieldsValue({ name: role.name, description: role.description, permissions: role.permissions || [] });
    setModalOpen(true);
  };

  const onSave = async (values) => {
    try {
      if (editing) {
        await axios.put(`${API_BASE_URL}/api/roles/${editing.id}`, values, { headers: authHeaders() });
        message.success('แก้ไขบทบาทเรียบร้อย');
      } else {
        await axios.post(`${API_BASE_URL}/api/roles`, values, { headers: authHeaders() });
        message.success('สร้างบทบาทเรียบร้อย');
      }
      setModalOpen(false);
      fetchRoles();
    } catch (err) {
      message.error(err.response?.data?.message || 'ไม่สามารถบันทึกบทบาทได้');
    }
  };

  const onDelete = async (id) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/roles/${id}`, { headers: authHeaders() });
      message.success('ลบบทบาทเรียบร้อย');
      fetchRoles();
    } catch (err) {
      message.error(err.response?.data?.message || 'ไม่สามารถลบบทบาทได้');
    }
  };

  const columns = [
    { title: 'ชื่อบทบาท', dataIndex: 'name', key: 'name' },
    { title: 'รหัส', dataIndex: 'code', key: 'code' },
    {
      title: 'จำนวนสิทธิ์',
      key: 'permCount',
      width: 110,
      render: (_, r) => <Tag color="gold">{Array.isArray(r.permissions) ? r.permissions.length : 0} สิทธิ์</Tag>,
    },
    {
      title: 'ประเภท',
      key: 'is_system',
      width: 100,
      render: (_, r) => (r.is_system ? <Tag color="blue">ระบบ</Tag> : <Tag>กำหนดเอง</Tag>),
    },
    {
      title: 'การจัดการ',
      key: 'action',
      width: 150,
      render: (_, r) => (
        <Space>
          <Button size="small" onClick={() => openEdit(r)} disabled={r.is_system}>แก้ไข</Button>
          <Popconfirm title="ลบบทบาทนี้?" onConfirm={() => onDelete(r.id)}>
            <Button size="small" danger disabled={r.is_system}>ลบ</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16, justifyContent: 'space-between', width: '100%' }}>
        <h2 style={{ margin: 0 }}>จัดการบทบาท/สิทธิ์</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>สร้างบทบาทใหม่</Button>
      </Space>
      <Table
        dataSource={roles}
        rowKey="id"
        columns={columns}
        pagination={false}
        scroll={{ x: 'max-content' }}
        expandable={{
          expandedRowRender: (r) => (
            <div>
              {Array.isArray(r.permissions) && r.permissions.length > 0 ? (
                r.permissions.map((p) => permissionTag(p))
              ) : (
                <span style={{ color: '#999' }}>ไม่มีสิทธิ์</span>
              )}
            </div>
          ),
        }}
      />

      <Modal
        title={editing ? 'แก้ไขบทบาท' : 'สร้างบทบาทใหม่'}
        open={modalOpen}
        onOk={() => form.submit()}
        onCancel={() => setModalOpen(false)}
        okText="บันทึก"
        cancelText="ยกเลิก"
        width={700}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onSave}>
          {!editing && (
            <Form.Item name="code" label="รหัสบทบาท (ภาษาอังกฤษ เช่น secretary)" rules={[{ required: true, message: 'กรุณากรอกรหัสบทบาท' }]}>
              <Input placeholder="เช่น secretary, assistant_region" />
            </Form.Item>
          )}
          <Form.Item name="name" label="ชื่อบทบาท" rules={[{ required: true, message: 'กรุณากรอกชื่อบทบาท' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="คำอธิบาย">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="permissions" label="สิทธิ์ของบทบาทนี้">
            <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #eee', borderRadius: 8, padding: 12 }}>
              {Object.entries(groups).map(([group, perms]) => (
                <div key={group} style={{ marginBottom: 10 }}>
                  <div style={{ fontWeight: 600, color: '#43290f', marginBottom: 4 }}>{group}</div>
                  <Checkbox.Group
                    options={perms.map((p) => ({ label: p.name, value: p.code }))}
                    style={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                  />
                </div>
              ))}
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default RoleManagement;

